import { AwaitContainer, Badge, Button, Item, ItemActions, ItemDescription, ItemHeader, ItemTitle } from '@ibsys/kit';
import { DownloadIcon } from 'lucide-react';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import {
  type DownloadTemplateEntityType,
  useDeleteDownloadTemplate,
  useDownloadTemplateFile,
  useDownloadTemplates,
} from '@/entities/download-template';
import { formatHumanDate } from '@/shared/lib';
import { ConfirmationButton } from '@/shared/ui';

type Props = {
  /** Сущность открытой вкладки: показываются только её шаблоны */
  entityType: DownloadTemplateEntityType;
};

/**
 * Перечень заведённых шаблонов сущности: наименование и дата появления. Поставленный системой
 * шаблон помечен, но во всём остальном равен загруженному — его так же скачивают и удаляют.
 */
export const DownloadTemplatesList: FC<Props> = ({ entityType }) => {
  const { t, i18n } = useTranslation(['downloadTemplates', 'common']);
  const { data, isLoading } = useDownloadTemplates(entityType);
  const { mutate: deleteTemplate, isPending: isDeletePending } = useDeleteDownloadTemplate();
  const {
    mutate: downloadFile,
    isPending: isDownloadPending,
    variables: downloadingTemplate,
  } = useDownloadTemplateFile();

  const templates = data?.templates ?? [];

  return (
    <AwaitContainer isLoading={isLoading}>
      {templates.length === 0 ? (
        <p className="text-sm text-muted-foreground">{t('downloadTemplates:list.empty')}</p>
      ) : (
        <div className="flex flex-col gap-3" data-testid="download-templates-list">
          {templates.map((template) => (
            <Item variant="outline" size="sm" key={template.id} data-testid={`download-template-${template.id}`}>
              <ItemHeader>
                <ItemTitle>{template.name}</ItemTitle>
                <ItemActions className="ml-4 gap-2">
                  {template.isSystem && (
                    <Badge variant="secondary" outline>
                      {t('downloadTemplates:list.system')}
                    </Badge>
                  )}
                  <Button
                    variant="ghost"
                    size="icon"
                    aria-label={t('downloadTemplates:list.downloadFile')}
                    tooltip={t('downloadTemplates:list.downloadFile')}
                    disabled={isDownloadPending && downloadingTemplate?.id === template.id}
                    icon={<DownloadIcon className="size-4" />}
                    onClick={() => downloadFile({ id: template.id, fileName: template.fileName })}
                  />
                  <ConfirmationButton
                    onConfirm={() => deleteTemplate({ id: template.id, entityType })}
                    isLoadingConfirm={isDeletePending}
                    title={t('downloadTemplates:list.deleteTitle')}
                    description={t('downloadTemplates:list.deleteDescription', { name: template.name })}
                  />
                </ItemActions>
              </ItemHeader>
              <ItemDescription className="text-sm text-muted-foreground">
                {t('downloadTemplates:list.description', {
                  date: formatHumanDate(i18n, template.createdAt),
                })}
              </ItemDescription>
            </Item>
          ))}
        </div>
      )}
    </AwaitContainer>
  );
};
